import React, { useContext, useState, useEffect } from 'react';
import { SignupContext } from './SignupContext';
import { phoneFormat } from '../../../api/utils/format/numbers';
import TextWinds from '../../../components/common/TextWinds';
import { useAppDispatch } from '../../../store/hook';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { User } from '../../../constants/types';
import { UserSnsType } from '../../../enum';
import { studentGuardianType } from '../../../constants/types/student-type';
import { useForm } from 'react-hook-form';
import ErrorInput from '../../student/components/ErrorInput';
import instance from '../../../api/axios/utils.ts/instance';
import { openAlert, openConfirm } from '../../../store/features/modalSlice';
import { check14UnderAge, uidRegExp } from '../../../api/utils/util-func';
import GuadianForm from './GuadianForm';
import { Input } from 'antd';
import HFbutton from '../../../components/common/input/HFbutton';
import FormRow from '../../../components/common/input/FormRow';
import {
  SIGNUP_MESSAGES,
  SIGNUP_PLACEHOLDERS,
  SIGNUP_LABELS,
  SIGNUP_BUTTONS,
} from './constants';
export type UserDTO = Pick<
  User,
  'uid' | 'email' | 'password' | 'name' | 'phone' | 'birthday' | 'gender'
> & {
  password2: string;
  snsType: UserSnsType;
  snsCode?: string;
  countryCode: string;
  agreementServiceUsage: boolean;
  agreementPersonalInfo: boolean;
  agreementLocationalInfo: boolean;
  guardianInfo: studentGuardianType;
};
const InfoForm = () => {
  const [searchParams] = useSearchParams();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { phone, step, setStep } = useContext(SignupContext);
  const [checkDuplicate, setCheckDuplicate] = useState<boolean>(false);

  // 가입중 체크
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const {
    control,
    handleSubmit,
    watch,
    clearErrors,
    formState: { errors },
  } = useForm<UserDTO>();
  const password = watch('password');
  const uid = watch('uid');

  const sns_code = searchParams.get('sns_code');
  const sns_type = searchParams.get('sns_type');

  // uid가 변경되면 중복확인 상태 초기화
  useEffect(() => {
    if (checkDuplicate) {
      setCheckDuplicate(false);
    }
  }, [uid]);

  const alert = (message: string) => dispatch(openAlert({ message: message }));

  const onSubmit = async (data: UserDTO) => {
    if (!checkDuplicate) {
      alert(SIGNUP_MESSAGES.DUPLICATE_CHECK_REQUIRED);
      control.setError('uid', { message: SIGNUP_MESSAGES.DUPLICATE_CHECK_REQUIRED });
      return;
    }

    if (check14UnderAge(data.birthday)) {
      if (!data.guardianInfo) {
        alert(SIGNUP_MESSAGES.GUARDIAN_INFO_REQUIRED);

        return;
      } else if (
        !data.guardianInfo.name ||
        !data.guardianInfo.gender ||
        !data.guardianInfo.birthday
      ) {
        alert(SIGNUP_MESSAGES.GUARDIAN_INFO_REQUIRED);
        return;
      } else if (!data.guardianInfo.phone) {
        alert(SIGNUP_MESSAGES.GUARDIAN_PHONE_AUTH_REQUIRED);
        return;
      }
    }

    const packet: UserDTO = {
      ...data,
      countryCode: '82',
      phone: phone,
      agreementLocationalInfo: true,
      agreementPersonalInfo: true,
      agreementServiceUsage: true,
      guardianInfo: check14UnderAge(data.birthday)
        ? data.guardianInfo
        : undefined,
    };

    if (sns_code && sns_type) {
      packet.snsCode = sns_code;
      packet.snsType = sns_type as UserSnsType;
    } else {
      packet.snsType = UserSnsType.DEFAULT;
    }

    try {
      setIsLoading(true);
      await postSignUp(packet);
      setStep(step + 1);
    } catch (error) {
      if (error instanceof Error) {
        alert(error.message);
      } else {
        alert(SIGNUP_MESSAGES.SIGNUP_FAILED);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const postSignUp = async (data: UserDTO) => {
    const response = await instance.post('/v1/auth/sign-up', data);
    const result = response.data;

    if (result.statusCode !== 200) {
      throw new Error(
        result.message ?? SIGNUP_MESSAGES.SIGNUP_FAILED,
      );
    }
  };

  // 아이디 중복확인
  const getIdCheck = async () => {
    if (!uid) {
      alert(SIGNUP_MESSAGES.UID_REQUIRED);
      control.setError('uid', { message: SIGNUP_MESSAGES.UID_REQUIRED });
      return;
    }

    if (!uid.match(uidRegExp)) {
      alert(SIGNUP_MESSAGES.UID_PATTERN_INVALID);
      control.setError('uid', { message: SIGNUP_MESSAGES.UID_PATTERN_INVALID });
      return;
    }

    const response = await instance.get('/v1/user/uid-check', {
      params: {
        uid: uid,
      },
    });
    const data = response.data;
    if (data.statusCode === 200) {
      if (data.data.existUser === false) {
        setCheckDuplicate(true);
        alert(SIGNUP_MESSAGES.UID_AVAILABLE);
        clearErrors('uid');
      } else {
        // 중복인 경우도 200으로 넘어옴
        setCheckDuplicate(false);
        alert(SIGNUP_MESSAGES.UID_DUPLICATE);
        // id input 중복 표시
        control.setError('uid', { message: SIGNUP_MESSAGES.UID_DUPLICATE });
      }
    } else {
      alert(SIGNUP_MESSAGES.UID_CHECK_FAILED);
    }
  };

  const handleCancel = () => {
    dispatch(
      openConfirm({
        message: SIGNUP_MESSAGES.CANCEL_CONFIRM,
        onConfirm: () => {
          navigate('/signin');
        },
        cancelText: SIGNUP_BUTTONS.CONTINUE_SIGNUP,
        confirmText: SIGNUP_BUTTONS.CANCEL_SIGNUP,
      }),
    );
  };

  return (
    <form className="flex flex-col gap-4">
      <section>
        <TextWinds type="title_h2">{SIGNUP_LABELS.TITLE}</TextWinds>
      </section>
      <section className="flex flex-col gap-2">
        <TextWinds type="caption1" children={SIGNUP_LABELS.PHONE} />
        <Input
          size="large"
          placeholder={SIGNUP_PLACEHOLDERS.PHONE}
          value={phoneFormat(phone)}
          disabled
        />

        <FormRow
          type="text-button"
          name="uid"
          className="p-3"
          rules={{
            required: SIGNUP_MESSAGES.UID_REQUIRED,
            minLength: {
              value: 5,
              message: SIGNUP_MESSAGES.UID_MIN_LENGTH,
            },
            maxLength: {
              value: 20,
              message: SIGNUP_MESSAGES.UID_MAX_LENGTH,
            },
            pattern: {
              value: uidRegExp,
              message: SIGNUP_MESSAGES.UID_PATTERN_DETAIL,
            },
            // 중복체크
            validate: (_) => {
              if (checkDuplicate) {
                return true;
              } else {
                return SIGNUP_MESSAGES.DUPLICATE_CHECK_REQUIRED;
              }
            },
          }}
          placeholder={SIGNUP_PLACEHOLDERS.UID}
          control={control}
          error={!!errors.uid}
          textButtonProp={{ text: SIGNUP_BUTTONS.DUPLICATE_CHECK, action: getIdCheck }}
        />
        {errors.uid && <ErrorInput text={errors.uid.message} />}

        <FormRow
          type="text"
          name="email"
          rules={{
            required: SIGNUP_MESSAGES.EMAIL_REQUIRED,
            pattern: {
              value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
              message: SIGNUP_MESSAGES.EMAIL_PATTERN_INVALID,
            },
          }}
          placeholder={SIGNUP_PLACEHOLDERS.EMAIL}
          control={control}
          error={!!errors.email}
        />
        {errors.email && <ErrorInput text={errors.email.message} />}

        <FormRow
          type="password"
          name="password"
          rules={{
            required: SIGNUP_MESSAGES.PASSWORD_REQUIRED,
            pattern: {
              value:
                /^(?=.*[A-Za-z])(?=.*\d)(?=.*[!@#$%^&*])[A-Za-z\d!@#$%^&*]{8,20}$/,
              message: SIGNUP_MESSAGES.PASSWORD_PATTERN_INVALID,
            },
          }}
          placeholder={SIGNUP_PLACEHOLDERS.PASSWORD}
          control={control}
          error={!!errors.password}
        />
        {errors.password && <ErrorInput text={errors.password.message} />}

        <FormRow
          type="password"
          name="password2"
          rules={{
            required: SIGNUP_MESSAGES.PASSWORD_CONFIRM_REQUIRED,
            validate: (value) =>
              value === password || SIGNUP_MESSAGES.PASSWORD_NOT_MATCH,
          }}
          placeholder={SIGNUP_PLACEHOLDERS.PASSWORD_CONFIRM}
          control={control}
          error={!!errors.password2}
        />
        {errors.password2 && <ErrorInput text={errors.password2.message} />}

        <FormRow
          type="text"
          name="name"
          rules={{
            required: SIGNUP_MESSAGES.NAME_REQUIRED,
            pattern: {
              value: /^[가-힣]{2,20}$/,
              message: SIGNUP_MESSAGES.NAME_PATTERN_INVALID,
            },
          }}
          placeholder={SIGNUP_PLACEHOLDERS.NAME}
          control={control}
          error={!!errors.name}
        />
        {errors.name && <ErrorInput text={errors.name.message} />}

        <FormRow
          name={'birthday'}
          type={'date'}
          rules={{ required: SIGNUP_MESSAGES.BIRTHDAY_REQUIRED }}
          control={control}
          placeholder={SIGNUP_PLACEHOLDERS.BIRTHDAY}
          error={!!errors.birthday}
        />
        {errors.birthday && <ErrorInput text={errors.birthday.message} />}

        <FormRow
          name="gender"
          type={'gender'}
          control={control}
          rules={{ required: SIGNUP_MESSAGES.GENDER_REQUIRED }}
        />
        {errors.gender && <ErrorInput text={errors.gender.message} />}
      </section>
      {check14UnderAge(watch('birthday')) && (
        <section>
          <GuadianForm control={control} />
        </section>
      )}
      <section className="flex flex-col gap-2">
        <HFbutton onClick={() => handleSubmit(onSubmit)()} disabled={isLoading}>
          {isLoading ? SIGNUP_BUTTONS.SIGNUP_IN_PROGRESS : SIGNUP_BUTTONS.SIGNUP}
        </HFbutton>
        <HFbutton type="Outlined" onClick={handleCancel}>
          {SIGNUP_BUTTONS.CANCEL}
        </HFbutton>
      </section>
    </form>
  );
};

export default InfoForm;
